/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : BarGraph.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography, Grid } from "@mui/material";
import React from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
} from "recharts";

function BarShape({ x, y, width, height, payload }) {
  return <rect x={x} y={y} width={width} height={height} fill={payload?.fill} />;
}

BarShape.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  payload: PropTypes.shape({ fill: PropTypes.string }),
};

const BarGraph = React.memo(({ data }) => {
  const chartData = data?.slice(1).map((item) => ({
    status: item[0],
    count: item[1],
    fill: item[2],
    annotation: item[4],
  }));

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 350,
        height: 350,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      <ResponsiveContainer
        width="90%"
        height="70%"
        sx={{
          pointerEvents: "none",
          display: "flex",
          justifyContent: "center",
          padding: 0,
          margin: 0,
          userSelect: "none",
        }}
      >
        <BarChart
          data={chartData}
          activeBar={false}
          accessibilityLayer={false}
          margin={{ top: 10, right: 20, left: -15, bottom: 10 }}
          style={{
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <CartesianGrid
            stroke="#eee"
            yAxis="viewBox"
            horizontal={true}
            vertical={false}
            style={{
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
          <XAxis
            dataKey="annotation"
            axisLine={{ stroke: "#000", strokeWidth: 1 }}
            tickLine={false}
            tick={{ fontSize: "7px" }}
            interval={0}
            style={{
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "#555" }}
            domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
            style={{
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
          <Bar
            dataKey="count"
            barSize={20}
            style={{
              pointerEvents: "none",
              userSelect: "none",
            }}
            tabIndex={-1}
            shape={<BarShape />}
          >
            <LabelList
              dataKey="count"
              position="top"
              style={{ fontSize: "7px", fill: "#1f1e1eff" }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 1,
          width: "100%",
        }}
      >
        <Grid
          container
          spacing={0.5}
          alignItems="center"
          sx={{ width: "100%", maxWidth: 300 }}
        >
          {chartData.map((item) => (
            <Grid item xs={4} key={item.status}>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: item.fill,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "6px",
                    textAlign: "left",
                  }}
                >
                  {item.status}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
});

BarGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.any),
};

export default BarGraph;
